import React, { useEffect, useMemo, useRef, useState, Children } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { User as UserIcon, Package, Mail, LogOut, ArrowRight, CheckCircle2, Clock, Truck, Download, AlertCircle, X, MapPin, Plus, Minus, Trash2, Edit2, Lock } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { attachOrderShippingAddress, getPreferredShippingAddress, recordProductSales, reconcileOrderInventory, subscribeToOrders, formatOrderNumber, type Address, type Order, type OrderLineItem, type ShippingAddress } from '../../../utils/firebase';
import { useCart } from '../../../contexts/CartContext';
import { getBookDownloadUrl } from '../../../utils/downloads';
import { subscribeToCatalogProducts, type CatalogProduct } from '../../../utils/firebase';
import { bookData } from './bookData';
const CHECKOUT_ADDRESS_SNAPSHOT_KEY = 'sn_checkout_shipping_address';
function readCheckoutAddressSnapshot(): {
  sessionId: string;
  address: ShippingAddress;
} | null {
  try {
    const raw = window.sessionStorage.getItem(CHECKOUT_ADDRESS_SNAPSHOT_KEY);
    if (!raw) return null;
    const snapshot = JSON.parse(raw) as {
      sessionId?: unknown;
      address?: Partial<ShippingAddress>;
    };
    if (typeof snapshot.sessionId !== 'string' || !snapshot.address || typeof snapshot.address.line1 !== 'string' || typeof snapshot.address.city !== 'string' || typeof snapshot.address.state !== 'string' || typeof snapshot.address.postalCode !== 'string' || typeof snapshot.address.country !== 'string') {
      return null;
    }
    return {
      sessionId: snapshot.sessionId,
      address: snapshot.address as ShippingAddress
    };
  } catch {
    return null;
  }
}
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}
function normalizeProductName(value: string | undefined): string {
  return (value ?? '').toLowerCase().replace(/(\d+)/g, '').split(/[^a-z]+/).filter(Boolean).sort().join('');
}
function getItemImage(item: OrderLineItem, products: CatalogProduct[]): string | undefined {
  const pid = item.productId;
  const normalizedName = normalizeProductName(item.name);
  if (item.type === 'book') {
    const book = bookData.find(b => b.id === pid) ?? bookData.find(b => b.lookupKey && b.lookupKey === pid) ?? bookData.find(b => b.stripePriceId && b.stripePriceId === pid) ?? bookData.find(b => normalizeProductName(b.title) === normalizedName);
    return book?.coverImage;
  }
  const product = products.find(s => s.id === pid) ?? products.find(s => s.lookupKey && s.lookupKey === pid) ?? products.find(s => {
    const catalogName = normalizeProductName(s.name);
    return catalogName === normalizedName || catalogName.startsWith(normalizedName) || normalizedName.startsWith(catalogName);
  }) ?? (normalizedName === 'omega' ? products.find(s => s.id === 'omega-3') : undefined);
  return product?.image;
}
function getItemShopId(item: OrderLineItem, products: CatalogProduct[]): string | undefined {
  const pid = item.productId;
  const normalizedName = normalizeProductName(item.name);
  if (item.type === 'book') return undefined;
  const product = products.find(s => s.id === pid) ?? products.find(s => s.lookupKey && s.lookupKey === pid) ?? products.find(s => {
    const catalogName = normalizeProductName(s.name);
    return catalogName === normalizedName || catalogName.startsWith(normalizedName) || normalizedName.startsWith(catalogName);
  }) ?? (normalizedName === 'omega' ? products.find(s => s.id === 'omega-3') : undefined);
  return product?.id;
}
function DownloadButton({
  orderId
}: {
  orderId: string;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleClick = async () => {
    setLoading(true);
    setError(null);
    try {
      const {
        url
      } = await getBookDownloadUrl(orderId);
      // Open the signed URL in a new tab so the user keeps their account page.
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      setError(err?.message ?? 'Download failed. Try again.');
    } finally {
      setLoading(false);
    }
  };
  return <div className="flex flex-col items-start gap-2" data-id="element-1714" data-mp="27ogws3xc01cz">
      <button type="button" onClick={handleClick} disabled={loading} className="px-5 py-3 bg-tealslate text-white text-xs font-bold uppercase tracking-wider hover:bg-navyslate disabled:opacity-60 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-2 rounded-md" data-id="element-1715" data-mp="27q1t9z37hudh">
        {loading ? <>
            <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1716" data-mp="3cuypblid8x0v" />
            Preparing…
          </> : <>
            <Download className="w-3.5 h-3.5" data-id="element-1717" data-mp="3ht9zjxckp9jm" />
            Download
          </>}
      </button>
      {error && <p className="text-[10px] text-burgundy leading-relaxed max-w-xs" data-id="element-1718" data-mp="3mmcuo9x00r1x">
          {error}
        </p>}
    </div>;
}
function FulfillmentBadge({
  order
}: {
  order: Order;
}) {
  const config = (() => {
    switch (order.fulfillmentStatus) {
      case 'digital_delivered':
        return {
          icon: CheckCircle2,
          label: 'Delivered',
          color: 'text-tealslate',
          bg: 'bg-tealslate/10'
        };
      case 'delivered':
        return {
          icon: CheckCircle2,
          label: 'Delivered',
          color: 'text-tealslate',
          bg: 'bg-tealslate/10'
        };
      case 'shipped':
        return {
          icon: Truck,
          label: 'Shipped',
          color: 'text-tealslate',
          bg: 'bg-tealslate/10'
        };
      case 'pending':
      default:
        return {
          icon: Clock,
          label: 'Processing',
          color: 'text-burgundy',
          bg: 'bg-burgundy/10'
        };
    }
  })();
  const Icon = config.icon;
  return <span className={`inline-flex items-center gap-1.5 px-3 py-1 ${config.bg} ${config.color} text-[10px] font-bold uppercase tracking-[0.2em] rounded-md`} data-id="element-1719" data-mp="282p2fszhvpp2">
      <Icon className="w-3 h-3" data-id="element-1720" data-mp="2i2zhlj53b76v" />
      {config.label}
    </span>;
}
function EditInfoModal({
  currentName,
  currentEmail,
  onClose,
  onSaveName,
  onSaveEmail
}: {
  currentName: string;
  currentEmail: string;
  onClose: () => void;
  onSaveName: (name: string) => Promise<void>;
  onSaveEmail: (email: string, password: string) => Promise<void>;
}) {
  const [name, setName] = useState(currentName);
  const [email, setEmail] = useState(currentEmail);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const emailChanged = email.trim().toLowerCase() !== currentEmail.toLowerCase();
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      if (name.trim() !== currentName) {
        await onSaveName(name);
      }
      if (emailChanged) {
        if (!password) {
          throw new Error('Current password is required to change email.');
        }
        await onSaveEmail(email, password);
      }
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err: any) {
      setError(err?.message ?? 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-id="element-1721" data-mp="2mqfnit8e27rf">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} onClick={onClose} className="absolute inset-0 bg-[#111111]/40 backdrop-blur-sm" data-id="element-1722" data-mp="2msjtdgpulguc" />
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} className="relative w-full max-w-md bg-white rounded-xl shadow-2xl overflow-hidden" data-id="element-1723" data-mp="2t820qpkayy3g">
        <div className="flex items-center justify-between p-6 border-b border-[#E5E5E5]" data-id="element-1724" data-mp="2ej5vt1djm172">
          <h2 className="text-xl font-bold text-[#111111]" data-id="element-1725" data-mp="2ehba3kug651x">Edit Information</h2>
          <button onClick={onClose} className="text-[#9A9A90] hover:text-[#111111] transition-colors" data-id="element-1726" data-mp="2ahspyrag9y05">
            <X className="w-5 h-5" data-id="element-1727" data-mp="25kamhgf7cazj" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5" data-id="element-1728" data-mp="20ibnsjueq28x">
          {success ? <div className="flex flex-col items-center justify-center py-8 text-center" data-id="element-1729" data-mp="1vjrzicsccede">
              <div className="w-12 h-12 bg-tealslate/10 rounded-full flex items-center justify-center mb-4" data-id="element-1730" data-mp="1lglhq8lps5g8">
                <CheckCircle2 className="w-6 h-6 text-tealslate" data-id="element-1731" data-mp="1qewr6i83kdxx" />
              </div>
              <p className="text-lg font-bold text-[#111111] mb-2" data-id="element-1732" data-mp="1vesnrwegt33g">
                Information Updated
              </p>
              {emailChanged && <p className="text-sm text-[#5A5A5A] leading-relaxed" data-id="element-1733" data-mp="20cb07wroy0g7">
                  A verification link has been sent to your new email address.
                  The change will take effect once you confirm it.
                </p>}
            </div> : <>
              <div data-id="element-1734" data-mp="1cob8atin8401">
                <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1735" data-mp="1cnrt3kkdntpp">
                  Full Name
                </label>
                <input type="text" required value={name} onChange={e => setName(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1736" data-mp="1hg2pwejtmze1" />
              </div>

              <div data-id="element-1737" data-mp="6qbeakduu8wi">
                <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1738" data-mp="bnbiepr591kz">
                  Email Address
                </label>
                <input type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1739" data-mp="gng2sqv6aggh" />
              </div>

              {emailChanged && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} className="overflow-hidden" data-id="element-1740" data-mp="3p2g9fvv90mb6">
                  <div className="pt-2" data-id="element-1741" data-mp="1qf9iixc2dfw">
                    <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1742" data-mp="31cvr9f6x5t2y">
                      Current Password *
                    </label>
                    <input type="password" required value={password} onChange={e => setPassword(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1743" data-mp="35cea85f6ju35" />
                    <p className="text-xs text-[#5A5A5A] mt-2 leading-relaxed" data-id="element-1744" data-mp="2ow4fkeewidev">
                      For security, your current password is required to change
                      your email address.
                    </p>
                  </div>
                </motion.div>}

              {error && <div className="flex items-start gap-2 p-3 bg-burgundy/5 rounded-md" data-id="element-1745" data-mp="2a3kc1cnua3ww">
                  <AlertCircle className="w-4 h-4 text-burgundy shrink-0 mt-0.5" data-id="element-1746" data-mp="256u113qw5yic" />
                  <p className="text-xs text-burgundy leading-relaxed" data-id="element-1747" data-mp="215gt8o48nl8s">
                    {error}
                  </p>
                </div>}

              <div className="pt-4" data-id="element-1748" data-mp="1w7f9e0yznhw9">
                <button type="submit" disabled={loading || !name.trim() && !emailChanged} className="w-full px-6 py-3.5 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors rounded-md shadow-sm disabled:opacity-60 flex items-center justify-center gap-2" data-id="element-1749" data-mp="1r5pyqmfoh68p">
                  {loading ? <>
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1750" data-mp="17lj1lqcv009m" />
                      Saving...
                    </> : 'Save Changes'}
                </button>
              </div>
            </>}
        </form>
      </motion.div>
    </div>;
}
function ChangePasswordModal({
  onClose,
  onChangePassword
}: {
  onClose: () => void;
  onChangePassword: (current: string, next: string) => Promise<void>;
}) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await onChangePassword(currentPassword, newPassword);
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err: any) {
      setError(err?.message ?? 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-id="element-1751" data-mp="2jgeay7cotzt">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} onClick={onClose} className="absolute inset-0 bg-[#111111]/40 backdrop-blur-sm" data-id="element-1752" data-mp="3unsexg7tysop" />
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} className="relative w-full max-w-md bg-white rounded-xl shadow-2xl overflow-hidden" data-id="element-1753" data-mp="19yl2yioaojcw">
        <div className="flex items-center justify-between p-6 border-b border-[#E5E5E5]" data-id="element-1754" data-mp="2e8eikyfmh133">
          <h2 className="text-xl font-bold text-[#111111]" data-id="element-1755" data-mp="2j9vfyjywe5ex">Change Password</h2>
          <button onClick={onClose} className="text-[#9A9A90] hover:text-[#111111] transition-colors" data-id="element-1756" data-mp="2o8yls6mpcfny">
            <X className="w-5 h-5" data-id="element-1757" data-mp="2s8p9g1gxmfo9" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5" data-id="element-1758" data-mp="2x0qtwao97cpd">
          {success ? <div className="flex flex-col items-center justify-center py-8 text-center" data-id="element-1759" data-mp="2x2tgslu40ppv">
              <div className="w-12 h-12 bg-tealslate/10 rounded-full flex items-center justify-center mb-4" data-id="element-1760" data-mp="321o9cfhvpsn6">
                <CheckCircle2 className="w-6 h-6 text-tealslate" data-id="element-1761" data-mp="36wtsx7nt6v83" />
              </div>
              <p className="text-lg font-bold text-[#111111] mb-2" data-id="element-1762" data-mp="3bxzrhm45hcpj">
                Password Changed
              </p>
              <p className="text-sm text-[#5A5A5A] leading-relaxed" data-id="element-1763" data-mp="1081oeisocx4b">
                Your password has been successfully updated.
              </p>
            </div> : <>
              <div data-id="element-1764" data-mp="qcgqavqq8gzu">
                <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1765" data-mp="qe1iwfthdpv2">
                  Current Password
                </label>
                <input type="password" required value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1766" data-mp="1j15j87hmpg0i" />
              </div>

              <div data-id="element-1767" data-mp="14bpsusm0fclc">
                <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1768" data-mp="14a59lz55bg2s">
                  New Password
                </label>
                <input type="password" required value={newPassword} onChange={e => setNewPassword(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1769" data-mp="1x059tkopc4zb" />
              </div>

              <div data-id="element-1770" data-mp="3hhwey7no3qs9">
                <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1771" data-mp="3hgleohtfl15w">
                  Confirm New Password
                </label>
                <input type="password" required value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1772" data-mp="38hht976sk9o8" />
              </div>

              {error && <div className="flex items-start gap-2 p-3 bg-burgundy/5 rounded-md" data-id="element-1773" data-mp="33j6ld202498n">
                  <AlertCircle className="w-4 h-4 text-burgundy shrink-0 mt-0.5" data-id="element-1774" data-mp="wtrvtnl0ltz0" />
                  <p className="text-xs text-burgundy leading-relaxed" data-id="element-1775" data-mp="11qid2e8k2vm0">
                    {error}
                  </p>
                </div>}

              <div className="pt-4" data-id="element-1776" data-mp="2m7hv6erzps0a">
                <button type="submit" disabled={loading || !currentPassword || !newPassword || !confirmPassword} className="w-full px-6 py-3.5 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors rounded-md shadow-sm disabled:opacity-60 flex items-center justify-center gap-2" data-id="element-1777" data-mp="2hb13jall0vig">
                  {loading ? <>
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1778" data-mp="34yisyzhdjhi9" />
                      Updating...
                    </> : 'Update Password'}
                </button>
              </div>
            </>}
        </form>
      </motion.div>
    </div>;
}
export function Account() {
  const {
    user,
    profile,
    loading: authLoading,
    signOut,
    resendVerification,
    updateDisplayName,
    updateEmail,
    changePassword
  } = useAuth();
  const navigate = useNavigate();
  const {
    items,
    clearCart
  } = useCart();
  const [products, setProducts] = useState<CatalogProduct[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const orderSuccess = searchParams.get('order') === 'success';
  const [showSuccess, setShowSuccess] = useState(orderSuccess);
  const [orders, setOrders] = useState<Order[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersError, setOrdersError] = useState<string | null>(null);
  const attemptedLegacyAddressSyncs = useRef(new Set<string>());
  const preferredShippingAddress = useMemo(() => getPreferredShippingAddress(profile?.addresses), [profile?.addresses]);
  // Keep the exact return-session address in component state. The reconciliation
  // effect can safely clear sessionStorage without making the order card blank
  // while Firestore catches up with the order-address write.
  const [checkoutAddressSnapshot] = useState(readCheckoutAddressSnapshot);
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [resendError, setResendError] = useState<string | null>(null);
  const [showEditInfo, setShowEditInfo] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [printingOrderId, setPrintingOrderId] = useState<string | null>(null);
  useEffect(() => {
    if (!printingOrderId) return;
    document.body.classList.add('printing-single-order');
    const successNotification = document.getElementById('order-success-notification');
    if (successNotification) {
      successNotification.style.display = 'none';
    }
    const timer = setTimeout(() => {
      window.print();
      setPrintingOrderId(null);
      document.body.classList.remove('printing-single-order');
    }, 100);
    return () => {
      clearTimeout(timer);
      document.body.classList.remove('printing-single-order');
      if (successNotification) {
        successNotification.style.display = '';
      }
    };
  }, [printingOrderId]);
  const rawTab = searchParams.get('tab') as 'profile' | 'orders' | 'digital' | 'addresses' | 'cart' | null;
  const initialTab: 'profile' | 'orders' | 'digital' | 'addresses' | 'cart' | null = rawTab;
  const [activeTab, setActiveTab] = useState<'profile' | 'orders' | 'digital' | 'addresses' | 'cart'>(initialTab || 'orders');
  useEffect(() => {
    if (initialTab && initialTab !== activeTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);
  const handleResendVerification = async () => {
    setResendState('sending');
    setResendError(null);
    try {
      await resendVerification();
      setResendState('sent');
      setTimeout(() => setResendState('idle'), 6000);
    } catch (err: any) {
      setResendState('error');
      setResendError(err?.message ?? 'Could not send verification email. Try again.');
    }
  };
  useEffect(() => {
    return subscribeToCatalogProducts(setProducts);
  }, []);
  useEffect(() => {
    if (!orderSuccess) return;

    // Capture purchased supplements before clearing the cart.
    const supplementSales = items.filter(i => i.type === 'supplement').map(i => ({
      productId: i.productId,
      quantity: i.quantity
    }));
    if (supplementSales.length > 0) {
      recordProductSales(supplementSales).catch(err => console.error('[Sales] Failed to record product sales:', err));
    }

    // A successful Stripe return means this cart has been purchased.
    // Clear the shared CartContext immediately so the cart tab/header badge
    // update everywhere in the app.
    clearCart();

    // Remove the success marker from the URL, but preserve session_id because
    // the order/inventory reconciliation logic may still need it.
    const next = new URLSearchParams(searchParams);
    next.delete('order');
    setSearchParams(next, {
      replace: true
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderSuccess]);
  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login', {
        state: {
          from: '/account'
        },
        replace: true
      });
    }
  }, [user, authLoading, navigate]);
  // Subscribe to orders
  useEffect(() => {
    if (!user) return;
    setOrdersLoading(true);
    setOrdersError(null);
    const unsub = subscribeToOrders(user.uid, next => {
      setOrders(next);
      setOrdersLoading(false);
    });
    return unsub;
  }, [user]);
  useEffect(() => {
    if (!preferredShippingAddress) return;
    const legacyOrders = orders.filter(order => order.status === 'paid' && !order.shippingAddress?.line1 && order.lineItems.some(item => item.type === 'supplement') && !attemptedLegacyAddressSyncs.current.has(order.id));
    legacyOrders.forEach(order => {
      attemptedLegacyAddressSyncs.current.add(order.id);
      attachOrderShippingAddress(order.id, preferredShippingAddress).catch(error => {
        console.error('[Orders] Could not backfill shipping address:', error);
      });
    });
  }, [orders, preferredShippingAddress]);
  useEffect(() => {
    const sessionId = window.sessionStorage.getItem('sn_inventory_checkout_session');
    if (!sessionId) return;
    const paidOrder = orders.find(order => order.status === 'paid' && order.stripeSessionId === sessionId);
    if (!paidOrder) return;
    const checkoutAddress = readCheckoutAddressSnapshot();
    const addressMatchesOrder = checkoutAddress?.sessionId === sessionId;
    const attachAddress = addressMatchesOrder && !paidOrder.shippingAddress?.line1 ? attachOrderShippingAddress(paidOrder.id, checkoutAddress.address) : Promise.resolve();
    Promise.all([reconcileOrderInventory(paidOrder), attachAddress]).then(() => {
      window.sessionStorage.removeItem('sn_inventory_checkout_session');
      if (addressMatchesOrder) {
        window.sessionStorage.removeItem(CHECKOUT_ADDRESS_SNAPSHOT_KEY);
      }
    }).catch(reconciliationError => {
      // Keep both checkout markers in place so a refresh retries safely.
      // Both writes are idempotent and never overwrite an existing address.
      console.error('[Checkout reconciliation] Failed:', reconciliationError);
    });
  }, [orders]);
  const handleSignOut = async () => {
    await signOut();
    navigate('/', {
      replace: true
    });
  };
  if (authLoading || !user) {
    return <div className="w-full min-h-screen bg-white flex items-center justify-center pt-32 pb-20" data-id="element-1779" data-mp="3alkeqxi1hypq">
        <span className="w-6 h-6 border-2 border-tealslate/30 border-t-tealslate rounded-full animate-spin" data-id="element-1780" data-mp="c1vcbgvqxhoj" />
      </div>;
  }
  const displayName = profile?.displayName ?? user.displayName ?? 'there';
  const firstName = displayName.split(' ')[0];
  const displayOrders = orders.map(o => {
    const hasPhysicalItems = o.lineItems.some(item => item.type === 'supplement');
    const checkoutAddress = checkoutAddressSnapshot?.sessionId === o.stripeSessionId ? checkoutAddressSnapshot.address : undefined;
    return {
      ...o,
      // Prefer the immutable order snapshot. If it is absent, use the exact
      // address from this checkout return, then a saved address for legacy
      // orders while the backfill above repairs the order record.
      shippingAddress: o.shippingAddress?.line1 || !hasPhysicalItems ? o.shippingAddress : checkoutAddress ?? preferredShippingAddress,
      lineItems: o.lineItems.filter(i => activeTab === 'digital' ? i.type === 'book' : i.type !== 'book')
    };
  }).filter(o => o.lineItems.length > 0);
  return <div className="w-full min-h-screen bg-white" data-id="element-1781" data-mp="1l2vkfdzncils">
      <AnimatePresence data-id="element-1782" data-mp="1l1km3toz0322">
        {showSuccess && <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} id="order-success-notification" className="fixed top-24 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-2xl print:hidden" data-id="element-1783" data-mp="1q1ysz8eu56yi">            <div className="bg-tealslate text-white shadow-2xl rounded-xl p-5 flex items-start gap-4" data-id="element-1784" data-mp="n0lx6a98tog">
              <CheckCircle2 className="w-6 h-6 shrink-0 mt-0.5" data-id="element-1785" data-mp="3orgsmerbzrdi" />
              <div className="flex-1" data-id="element-1786" data-mp="3ott90nhfxtog">
                <p className="font-bold text-lg mb-1" data-id="element-1787" data-mp="3kqn5hnlpqglo">Order received.</p>
                <p className="text-sm text-white/90 leading-relaxed" data-id="element-1788" data-mp="3kshtjasa6otz">
                  Thanks for your purchase — your order will appear below in a
                  moment. A receipt has been emailed to you.
                </p>
              </div>
              <button onClick={() => setShowSuccess(false)} className="text-white/70 hover:text-white transition-colors" aria-label="Dismiss" data-id="element-1789" data-mp="bk5e9kq9wvfp">
                <X className="w-4 h-4" data-id="element-1790" data-mp="7hsj1q95kfx6" />
              </button>
            </div>
          </motion.div>}

        {showEditInfo && <EditInfoModal currentName={displayName} currentEmail={user.email ?? ''} onClose={() => setShowEditInfo(false)} onSaveName={updateDisplayName} onSaveEmail={updateEmail} data-id="element-1791" data-mp="gc6vzm67ned4" />}

        {showChangePassword && <ChangePasswordModal onClose={() => setShowChangePassword(false)} onChangePassword={changePassword} data-id="element-1792" data-mp="qacxw3s0okhz" />}
      </AnimatePresence>

      {/* HEADER */}
      <section className="account-header pt-32 pb-8 px-6" data-id="element-1793" data-mp="19wo6q3yz7trw">
        <div className="max-w-5xl mx-auto" data-id="element-1794" data-mp="1dxrowx4hy1wa">
          <motion.div initial="hidden" animate="visible" variants={{
          visible: {
            transition: {
              staggerChildren: 0.1
            }
          }
        }} data-id="element-1795" data-mp="1dvdu8pdqarp9">
            <motion.h1 variants={fadeUp} className="text-3xl md:text-4xl font-bold text-[#111111] mb-2 tracking-tight" data-id="element-1796" data-mp="14uyuwm3xjc2r">
              Welcome back, {firstName}!
            </motion.h1>
            <motion.p variants={fadeUp} className="text-sm font-medium text-[#5A5A5A]" data-id="element-1797" data-mp="uzfcn2iagbwh">
              Manage your profile and orders
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="pb-24 px-6" data-id="element-1798" data-mp="m47re16mifwa">
        <div className="max-w-5xl mx-auto" data-id="element-1799" data-mp="78jg5s2w0siw">
          {/* MAIN CONTENT */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="w-full" data-id="element-1800" data-mp="c84mwooau566">
            <div className="account-tabs flex items-center gap-4 border-b border-[#E5E5E5] mb-8 overflow-x-auto hide-scrollbar" data-id="element-1801" data-mp="rpcn97saatf6">
              <button onClick={() => {
              setActiveTab('profile');
              setSearchParams({
                tab: 'profile'
              });
            }} className={`account-tab-button pb-4 text-sm font-bold border-b-2 transition-colors whitespace-nowrap ${activeTab === 'profile' ? 'active border-[#111111] text-[#111111]' : 'border-transparent text-[#0056b3] hover:text-[#111111]'}`} data-id="element-1802" data-mp="iphch42n50fo">
                Profile
              </button>
              <button onClick={() => {
              setActiveTab('addresses');
              setSearchParams({
                tab: 'addresses'
              });
            }} className={`account-tab-button pb-4 text-sm font-bold border-b-2 transition-colors whitespace-nowrap ${activeTab === 'addresses' ? 'active border-[#111111] text-[#111111]' : 'border-transparent text-[#0056b3] hover:text-[#111111]'}`} data-id="element-1803" data-mp="3w4vofjcxo24p">
                Addresses
              </button>
              <button onClick={() => {
              setActiveTab('orders');
              setSearchParams({});
            }} className={`account-tab-button pb-4 text-sm font-bold border-b-2 transition-colors whitespace-nowrap ${activeTab === 'orders' ? 'active border-[#111111] text-[#111111]' : 'border-transparent text-[#0056b3] hover:text-[#111111]'}`} data-id="element-1804" data-mp="3c1q17r56oiyw">
                Orders
              </button>
              <button onClick={() => {
              setActiveTab('digital');
              setSearchParams({
                tab: 'digital'
              });
            }} className={`account-tab-button pb-4 text-sm font-bold border-b-2 transition-colors whitespace-nowrap ${activeTab === 'digital' ? 'active border-[#111111] text-[#111111]' : 'border-transparent text-[#0056b3] hover:text-[#111111]'}`} data-id="element-1805" data-mp="2taynkde5y6ep">
                Digital Orders
              </button>
              <button onClick={() => {
              setActiveTab('cart');
              setSearchParams({
                tab: 'cart'
              });
            }} className={`account-tab-button pb-4 text-sm font-bold border-b-2 transition-colors whitespace-nowrap ${activeTab === 'cart' ? 'active border-[#111111] text-[#111111]' : 'border-transparent text-[#0056b3] hover:text-[#111111]'}`} data-id="element-1806" data-mp="2aixhapz27dv9">
                Cart
              </button>
            </div>

            {activeTab === 'profile' ? <div className="space-y-8" data-id="element-1807" data-mp="102a4fd7mf9xf">
                <div className="max-w-2xl space-y-4" data-id="element-1808" data-mp="14vlgptv7ciwq">
                  <div className="flex items-center justify-between gap-4" data-id="element-1809" data-mp="14x68jbq4tnqw">
                    <h2 className="text-2xl font-bold text-[#111111] tracking-tight" data-id="element-1810" data-mp="q7qlo6tq22gh">
                      Account Information
                    </h2>
                    <button onClick={() => setShowEditInfo(true)} className="shrink-0 text-xs font-bold py-2 px-3 bg-white border border-[#E5E5E5] rounded-md text-[#111111] hover:border-tealslate hover:text-tealslate transition-colors flex items-center gap-2 shadow-sm" aria-label="Edit account information" data-id="element-1811" data-mp="h5r49desowd0">
                      <Edit2 className="w-3.5 h-3.5" data-id="element-1812" data-mp="3tpmp6cg3esuo" />
                      Edit
                    </button>
                  </div>

                  <div className="bg-white border border-[#D1D1D1] rounded-md p-6 md:p-8" data-id="element-1813" data-mp="bl1nc8b7hvbo">
                    <dl className="divide-y divide-[#E5E5E5]" data-id="element-1814" data-mp="6jm3gjkrluuv">
                      <div className="flex items-center justify-between gap-4 py-3 first:pt-0" data-id="element-1815" data-mp="lhed4v3mrp15">
                        <dt className="text-xs font-bold uppercase tracking-wider text-[#9A9A90]" data-id="element-1816" data-mp="gitbqscn6zb4">
                          Name
                        </dt>
                        <dd className="text-sm font-medium text-[#111111] text-right" data-id="element-1817" data-mp="u96k2vtg4g0z">
                          {displayName}
                        </dd>
                      </div>
                      <div className="flex items-center justify-between gap-4 py-3" data-id="element-1818" data-mp="148nprd8p6twy">
                        <dt className="text-xs font-bold uppercase tracking-wider text-[#9A9A90]" data-id="element-1819" data-mp="za45ridnzs78">
                          Email
                        </dt>
                        <dd className="text-sm font-medium text-[#111111] text-right break-all flex items-center gap-2" data-id="element-1820" data-mp="x8b7xht1djs">
                          <Mail className="w-4 h-4 text-[#9A9A90] shrink-0" data-id="element-1821" data-mp="5w30niaa20cv" />
                          {user.email}
                        </dd>
                      </div>
                      <div className="flex items-center justify-between gap-4 py-3 last:pb-0" data-id="element-1822" data-mp="fm4p4b6jlksb">
                        <dt className="text-xs font-bold uppercase tracking-wider text-[#9A9A90]" data-id="element-1823" data-mp="kl7l764jogo5">
                          Password
                        </dt>
                        <dd className="flex items-center gap-3" data-id="element-1824" data-mp="oncaxtrvgt5i">
                          <span className="text-sm font-medium text-[#111111] tracking-widest" data-id="element-1825" data-mp="tm5qdrqpyu8b">
                            ••••••••
                          </span>
                          <button onClick={() => setShowChangePassword(true)} className="account-action-button text-xs font-bold transition-colors" data-id="element-1826" data-mp="2vdaggjd07mqj">
                            Change
                          </button>
                        </dd>
                      </div>
                    </dl>

                    {!user.emailVerified && <div className="mt-6 pt-6 border-t border-[#E5E5E5]" data-id="element-1827" data-mp="2e3eyvgz41zst">
                        <div className="flex items-start gap-2 mb-4" data-id="element-1828" data-mp="294val9x1obxa">
                          <AlertCircle className="w-4 h-4 text-burgundy shrink-0 mt-0.5" data-id="element-1829" data-mp="296oguo3qwu4r" />
                          <p className="text-xs text-[#5A5A5A] leading-relaxed" data-id="element-1830" data-mp="36s3rssq0iyth">
                            Email not verified. Check your inbox for a
                            verification link.
                          </p>
                        </div>
                        <button onClick={handleResendVerification} disabled={resendState === 'sending' || resendState === 'sent'} className="w-full text-xs font-bold py-3 px-4 border border-[#E5E5E5] rounded-md text-[#111111] hover:border-tealslate hover:text-tealslate disabled:opacity-60 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2" data-id="element-1831" data-mp="2stdubbza6x4l">
                          {resendState === 'sending' && <>
                              <span className="w-3.5 h-3.5 border-2 border-current/30 border-t-current rounded-full animate-spin" data-id="element-1832" data-mp="3a2og5ru2d9me" />
                              Sending…
                            </>}
                          {resendState === 'sent' && <>
                              <CheckCircle2 className="w-4 h-4" data-id="element-1833" data-mp="3o0czgv8hg8pc" />
                              Verification email sent
                            </>}
                          {(resendState === 'idle' || resendState === 'error') && <>
                              <Mail className="w-4 h-4" data-id="element-1834" data-mp="3ezn7y3q994hf" />
                              Resend verification email
                            </>}
                        </button>
                        {resendState === 'error' && resendError && <p className="text-xs text-burgundy mt-3 leading-relaxed" data-id="element-1835" data-mp="23n4d70gxfngk">
                            {resendError}
                          </p>}
                        {resendState === 'sent' && <p className="text-xs text-[#9A9A90] mt-3 leading-relaxed" data-id="element-1836" data-mp="201994nszdce8">
                            Check your inbox (and spam folder). The link expires
                            in 24 hours.
                          </p>}
                      </div>}
                  </div>
                </div>

                <button onClick={handleSignOut} className="w-full max-w-2xl bg-white border border-[#D1D1D1] shadow-sm rounded-xl p-5 hover:border-tealslate transition-all group flex items-center justify-between" data-id="element-1837" data-mp="252yp8hv28g96">
                  <span className="text-sm font-bold group-hover:text-tealslate transition-colors text-[#FFFFFF]" data-id="element-1838" data-mp="1cboq64wtuf4y">
                    Sign Out
                  </span>
                  <LogOut className="w-4 h-4 text-[#9A9A90] group-hover:text-tealslate transition-colors" data-id="element-1839" data-mp="1ma4j8dfezyh3" />
                </button>
              </div> : activeTab === 'addresses' ? <AddressBookTab data-id="element-1840" data-mp="tntin2rt5xka" /> : activeTab === 'orders' || activeTab === 'digital' ? <>
                {ordersLoading ? <div className="bg-white border border-[#E5E5E5] rounded-md p-12 flex items-center justify-center" data-id="element-1841" data-mp="1ja4c2imxvfse">
                    <span className="w-6 h-6 border-2 border-tealslate/30 border-t-tealslate rounded-full animate-spin" data-id="element-1842" data-mp="1nb7pyzlff1qz" />
                  </div> : ordersError ? <div className="bg-white border border-[#E5E5E5] rounded-md p-6 flex gap-3 items-start" data-id="element-1843" data-mp="1x2kgnnvhnlvf">
                    <AlertCircle className="w-5 h-5 text-burgundy shrink-0 mt-0.5" data-id="element-1844" data-mp="10cl0b793jj2a" />
                    <p className="text-sm text-burgundy" data-id="element-1845" data-mp="14dwrvibj6ed4">{ordersError}</p>
                  </div> : displayOrders.length === 0 ? <div className="bg-white border border-[#E5E5E5] rounded-md p-12 text-center" data-id="element-1846" data-mp="19d9eq32ejlfp">
                    <Package className="w-12 h-12 text-[#9A9A90] mx-auto mb-4" data-id="element-1847" data-mp="1ebt1u6sqf39n" />
                    <h3 className="text-xl font-bold text-[#111111] mb-2" data-id="element-1848" data-mp="1e9q2gw5ovxs9">
                      {activeTab === 'digital' ? 'No digital orders yet.' : 'No physical orders yet.'}
                    </h3>
                    <p className="text-sm text-[#5A5A5A] leading-relaxed mb-8 max-w-sm mx-auto" data-id="element-1849" data-mp="2pt2kws7y7taf">
                      When you make a purchase, your orders will appear here.
                    </p>
                    <div className="flex justify-center" data-id="element-1850" data-mp="975nzlsdl3he">
                      <Link to="/shop" className="px-6 py-3.5 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors inline-flex items-center justify-center gap-2 rounded-md shadow-sm" data-id="element-1851" data-mp="4crwvvkqxiij">
                        Shop Supplements <ArrowRight className="w-4 h-4" data-id="element-1852" data-mp="3rgpp2ufpq58v" />
                      </Link>
                    </div>
                  </div> : <ul className="space-y-6" data-id="element-1853" data-mp="3hkmovu4f8gok">
                    {displayOrders.map(order => <li key={order.id} className={`order-item bg-white border border-[#D1D1D1] rounded-md overflow-hidden ${printingOrderId === order.id ? 'is-printing' : ''}`} data-id="element-1854" data-mp="3hmpbe46d7roj">
                        {/* Header Strip */}
                        <div className="bg-[#F9F9F9] px-6 py-4 flex flex-wrap items-start justify-between gap-6" data-id="element-1855" data-mp="1au5aedbma91n">
                          <div className="flex flex-wrap items-start gap-8 md:gap-12" data-id="element-1856" data-mp="joatu7z2gpd5">
                            <div data-id="element-1857" data-mp="onmdppuqcayy">
                              <p className="font-bold text-[#111111] uppercase tracking-wider mb-1 text-[12px]" data-id="element-1858" data-mp="omujaicvcja9">
                                Order Placed
                              </p>
                              <p className="text-sm text-[#111111]" data-id="element-1859" data-mp="ex14l8hfjo1s">
                                {formatDate(order.createdAt)}
                              </p>
                            </div>
                            <div data-id="element-1860" data-mp="5yfqbpizqv6t">
                              <p className="font-bold text-[#111111] uppercase tracking-wider mb-1 text-[12px]" data-id="element-1861" data-mp="5ypinjridefo">
                                Total
                              </p>
                              <p className="text-sm text-[#111111]" data-id="element-1862" data-mp="3s54j7qn155w7">
                                {formatPrice(order.total)}
                              </p>
                            </div>
                            {order.shippingAddress?.line1 && <>
                                {order.shippingAddress.recipientName && <div className="not-italic max-w-[220px]" data-id="element-1863" data-mp="3avtj0xusdaoj">
                                    <p className="font-bold text-[#111111] uppercase tracking-wider mb-1 text-[12px]" data-id="element-1864" data-mp="36uq4qfsdzmpf">
                                      Ship To
                                    </p>
                                    <p className="text-sm font-medium text-[#111111]" data-id="element-1865" data-mp="3ftlbpu3990ts">
                                      {order.shippingAddress.recipientName}
                                    </p>
                                  </div>}
                                <address className="not-italic max-w-[220px]" data-id="element-1866" data-mp="3pmiy4bbdb9xo">
                                  <p className="font-bold text-[#111111] uppercase tracking-wider mb-1 text-[12px]" data-id="element-1867" data-mp="7ds64aagp9ri">
                                    Address
                                  </p>
                                  <p className="text-[#111111] text-[14px] w-[350px]" data-id="element-1868" data-mp="2966lb1lmg048">
                                    {order.shippingAddress.line1}
                                    {order.shippingAddress.line2 && <> {order.shippingAddress.line2}</>}
                                    <br data-id="element-1869" data-mp="ff2p14nq2l1z" />
                                    {order.shippingAddress.city},{' '}
                                    {order.shippingAddress.state}{' '}
                                    {order.shippingAddress.postalCode}
                                  </p>
                                </address>
                              </>}
                          </div>
                          <div className="text-right" data-id="element-1870" data-mp="ydygw9c3yncn">
                            <p className="font-bold text-[#111111] uppercase tracking-wider mb-1 text-[12px]" data-id="element-1871" data-mp="yb2i6isfrun5">
                              Order # {formatOrderNumber(order.id)}
                            </p>
                            <button onClick={() => setPrintingOrderId(order.id)} className="order-details-button print-button text-sm text-[#0056b3] hover:text-[#004494] hover:underline font-medium transition-colors" data-id="element-1872" data-mp="185cda88rj71k">
                              Order Details
                            </button>
                          </div>
                        </div>

                        {/* Body */}
                        <div className="p-6 flex flex-col md:flex-row items-center justify-between gap-8" data-id="element-1873" data-mp="25m6ssw2p85zl">
                          
                          <div className="flex-1 w-full" data-id="element-1874" data-mp="1vpkaof1rbt19">
                            {(order.fulfillmentStatus === 'delivered' || order.fulfillmentStatus === 'digital_delivered' || order.fulfillmentStatus === 'shipped' || order.trackingNumber) && <div className="mb-6" data-id="element-1875" data-mp="1mqgoh27amxij">
                                {(order.fulfillmentStatus === 'delivered' || order.fulfillmentStatus === 'digital_delivered' || order.fulfillmentStatus === 'shipped') && <h3 className="text-lg font-bold text-tealslate mb-1" data-id="element-1876" data-mp="1d0x2vos74pjz">
                                    {order.fulfillmentStatus === 'shipped' ? 'Shipped' : 'Delivered'}
                                  </h3>}
                                {order.trackingNumber && <p className="text-sm text-[#5A5A5A] flex items-center gap-1.5" data-id="element-1877" data-mp="35ocxaiuupg9s">
                                    <Truck className="w-4 h-4" data-id="element-1878" data-mp="30xmvfflmvvju" />
                                    {order.trackingCarrier ?? 'Tracking'} ·{' '}
                                    {order.trackingNumber}
                                  </p>}
                              </div>}

                            <ul className="space-y-8" data-id="element-1879" data-mp="2qxdtdl4ynfwj">
                              {order.lineItems.map((item, idx) => {
                        const image = getItemImage(item, products);
                        return <li key={idx} className="flex flex-col sm:flex-row items-center gap-6" data-id="element-1880" data-mp="3joxg3f0rnc3a" data-mpi={idx}>
                                    {/* Left side: image */}
                                    {image && <div className="w-28 h-28 sm:w-32 sm:h-32 shrink-0 flex items-center justify-center" data-id="element-1881" data-mp="3amo9v1cj64vp">
                                        <img src={image} alt={item.name} className="max-w-full max-h-full object-contain" data-id="element-1882" data-mp="237ruyzwex4pl" />
                                      </div>}

                                    {/* Right side: details and actions */}
                                    <div className="flex-1" data-id="element-1883" data-mp="irc0mwpb7qx9">
                                    {item.type === 'book' ? <Link to="/shop" className="font-bold text-[#0056b3] hover:text-[#004494] hover:underline text-base mb-1 block transition-colors" data-id="element-1884" data-mp="nqwvow1czf4p">
                                        {item.name}
                                      </Link> : <Link to={`/shop/${getItemShopId(item, products) || item.productId}`} className="font-bold text-[#0056b3] hover:text-[#004494] hover:underline text-base mb-1 block transition-colors" data-id="element-1885" data-mp="xfolaxiy5ncg">
                                        {item.name}
                                      </Link>}
                                      <p className="text-sm text-[#5A5A5A] mb-4" data-id="element-1886" data-mp="1bfzcy2s2z5yo">
                                        Qty {item.quantity}
                                        {item.type === 'book' && ' · Digital'}{' '}
                                        ·{' '}
                                        {formatPrice(item.unitPrice * item.quantity)}
                                      </p>

                                      {item.type === 'book' && order.status === 'paid' && <DownloadButton orderId={order.id} data-id="element-1887" data-mp="2rvlncfs5e8x2" />}
                                    </div>
                                  </li>;
                      })}
                            </ul>
                          </div>

                          {/* Order Summary */}
                          <div className="w-full sm:w-80 shrink-0 bg-[#FFFFFF] p-5" data-id="element-1888" data-mp="1u84xjha3b2vh">
                            <h3 className="font-bold text-[#111111] mb-4 font-[Avenir,_sans-serif]" data-id="element-1889" data-mp="1u6a9vv7fownp">
                              Order Summary
                            </h3>

                            <div className="space-y-3 text-sm" data-id="element-1890" data-mp="244ztptiw4r6l">
                              <div className="flex justify-between text-[#5A5A5A]" data-id="element-1891" data-mp="1gfon3vev02kj">
                                <span data-id="element-1892" data-mp="1bi5at11q1zv8">Subtotal</span>
                                <span data-id="element-1893" data-mp="1bfj391etzkd0">{formatPrice(order.subtotal)}</span>
                              </div>

<div className="flex justify-between text-[#5A5A5A]" data-id="element-1894" data-mp="3n3e6kszcqsgb">
  <span data-id="element-1895" data-mp="3rvfgw9cym93p">Shipping</span>
  <span data-id="element-1896" data-mp="3rwqtmyodur6y">
    {(order.shipping ?? 0) > 0 ? formatPrice(order.shipping ?? 0) : 'Free'}
  </span>
</div>

                              <div className="flex justify-between text-[#5A5A5A]" data-id="element-1897" data-mp="3d4q1svwdo9uk">
                                <span data-id="element-1898" data-mp="3i65m2lqqecbw">Tax</span>
                                <span data-id="element-1899" data-mp="9qc7wdjvhcy0">{formatPrice(order.tax ?? 0)}</span>
                              </div>

                              {(order.discount ?? 0) > 0 && <div className="flex justify-between text-[#5A5A5A]" data-id="element-1900" data-mp="eonf0gis99cj">
                                  <span data-id="element-1901" data-mp="ocq3j11a4u0">Discount</span>
                                  <span data-id="element-1902" data-mp="4qzhxjxpfpr7">-{formatPrice(order.discount ?? 0)}</span>
                                </div>}

                              <div className="pt-3 border-t border-[#E5E5E5] flex justify-between font-bold text-[#111111]" data-id="element-1903" data-mp="2lf3nell31feg">
                                <span data-id="element-1904" data-mp="2pi00xxvrmlbj">Grand Total</span>
                                <span data-id="element-1905" data-mp="vyhtmg1gxath">{formatPrice(order.total)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>)}
                  </ul>}
              </> : <CartTab data-id="element-1906" data-mp="m7dffijm9tzp" />}
          </motion.div>
        </div>
      </section>
    </div>;
}
function AddressBookTab() {
  const {
    profile,
    updateProfile
  } = useAuth();
  const addresses = profile?.addresses ?? [];
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const handleSetDefault = async (id: string) => {
    const updated = addresses.map(a => ({
      ...a,
      isDefault: a.id === id
    }));
    await updateProfile({
      addresses: updated
    });
  };
  const handleDelete = async (id: string) => {
    const updated = addresses.filter(a => a.id !== id);
    // If we deleted the default, make the first remaining one default
    if (addresses.find(a => a.id === id)?.isDefault && updated.length > 0) {
      updated[0].isDefault = true;
    }
    await updateProfile({
      addresses: updated
    });
  };
  if (isAdding || editingId) {
    const addressToEdit = editingId ? addresses.find(a => a.id === editingId) : null;
    return <AddressForm initialData={addressToEdit} onCancel={() => {
      setIsAdding(false);
      setEditingId(null);
    }} onSave={async addr => {
      let updated: Address[];
      if (editingId) {
        updated = addresses.map(a => a.id === editingId ? {
          ...addr,
          id: editingId,
          isDefault: a.isDefault
        } : a);
      } else {
        const isFirst = addresses.length === 0;
        updated = [...addresses, {
          ...addr,
          id: Math.random().toString(36).substring(2, 9),
          isDefault: isFirst
        }];
      }
      await updateProfile({
        addresses: updated
      });
      setIsAdding(false);
      setEditingId(null);
    }} data-id="element-1907" data-mp="2a05vh6kzf6on" />;
  }
  return <div className="space-y-6" data-id="element-1908" data-mp="37no3ugaqe6s4">
      <div className="flex items-center justify-between" data-id="element-1909" data-mp="3sp41auzf6wjc">
        <h2 className="text-2xl font-bold text-[#111111] tracking-tight" data-id="element-1910" data-mp="3snj6d4971bky">
          Saved Addresses
        </h2>
        <button onClick={() => setIsAdding(true)} className="px-4 py-2 bg-tealslate text-white text-xs font-bold uppercase tracking-wider hover:bg-navyslate transition-colors rounded-md shadow-sm flex items-center gap-2" data-id="element-1911" data-mp="6aaxbbtbbc7p">
          <Plus className="w-4 h-4" data-id="element-1912" data-mp="g7z0mq4zhs1h" />
          Add New
        </button>
      </div>

      {addresses.length === 0 ? <div className="bg-white border border-[#E5E5E5] rounded-md p-12 text-center" data-id="element-1913" data-mp="ba739glkldpw">
          <MapPin className="w-12 h-12 text-[#9A9A90] mx-auto mb-4" data-id="element-1914" data-mp="p9or9tr1kmy7" />
          <h3 className="text-xl font-bold text-[#111111] mb-2" data-id="element-1915" data-mp="p6stc5f71y9r">
            No saved addresses.
          </h3>
          <p className="text-sm text-[#5A5A5A] leading-relaxed mb-8 max-w-sm mx-auto" data-id="element-1916" data-mp="2r0a076vi2v3g">
            Add a shipping address to speed up your checkout process.
          </p>
          <button onClick={() => setIsAdding(true)} className="px-6 py-3.5 bg-white border border-[#E5E5E5] text-[#111111] text-sm font-bold hover:border-tealslate hover:text-tealslate transition-colors inline-flex items-center justify-center gap-2 rounded-md shadow-sm" data-id="element-1917" data-mp="2m3jnmtiwmhms">
            Add Address
          </button>
        </div> : <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1918" data-mp="wsobg1mbuiim">
          {addresses.map(addr => <div key={addr.id} className="bg-white border border-[#D1D1D1] rounded-md p-6 flex flex-col" data-id="element-1919" data-mp="wqblp2abv3v0">
              <div className="flex-1" data-id="element-1920" data-mp="nrzzdcghus8d">
                <div className="flex items-start justify-between mb-2" data-id="element-1921" data-mp="nt17p964l0me">
                  <p className="font-bold text-[#111111]" data-id="element-1922" data-mp="9si6om02uutq">
                    {addr.recipientName}
                  </p>
                  {addr.isDefault && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm font-bold text-[#FFFFFF] bg-[#172F3D]" data-id="element-1923" data-mp="duc1weri76mi">
                      Default
                    </span>}
                </div>
                <p className="text-sm text-[#5A5A5A] leading-relaxed mb-6" data-id="element-1924" data-mp="2871u8s2847rs">
                  {addr.line1}
                  {addr.line2 && <br data-id="element-1925" data-mp="3fw39eakqb7pf" />}
                  {addr.line2}
                  <br data-id="element-1926" data-mp="3fwv7bs1sthiv" />
                  {addr.city}, {addr.state} {addr.postalCode}
                  <br data-id="element-1927" data-mp="3jwm2s929sltq" />
                  {addr.country}
                </p>
              </div>
              <div className="flex items-center gap-4 pt-4 border-t border-[#E5E5E5]" data-id="element-1928" data-mp="3opf931de027x">
                <button onClick={() => setEditingId(addr.id)} className="account-action-button text-sm font-bold text-[#5A5A5A] hover:text-tealslate transition-colors flex items-center gap-1.5" data-id="element-1929" data-mp="3to01iemedhlt">
                  <Edit2 className="w-3.5 h-3.5" data-id="element-1930" data-mp="7fqo7gprd4ca" /> Edit
                </button>
                <button onClick={() => handleDelete(addr.id)} className="account-action-button delete-action text-sm font-bold text-[#5A5A5A] hover:text-burgundy transition-colors flex items-center gap-1.5" data-id="element-1931" data-mp="bhvafudvmyow">
                  <Trash2 className="w-3.5 h-3.5" data-id="element-1932" data-mp="2d8yn0p7bunc3" /> Delete
                </button>
                {!addr.isDefault && <button onClick={() => handleSetDefault(addr.id)} className="account-action-button ml-auto text-sm font-bold text-tealslate hover:text-navyslate transition-colors" data-id="element-1933" data-mp="25q8xwjby1p48">
                    Set as Default
                  </button>}
              </div>
            </div>)}
        </div>}
    </div>;
}
function AddressForm({
  initialData,
  onCancel,
  onSave
}: {
  initialData: Address | null;
  onCancel: () => void;
  onSave: (addr: Omit<Address, 'id' | 'isDefault'>) => Promise<void>;
}) {
  const [recipientName, setRecipientName] = useState(initialData?.recipientName ?? '');
  const [line1, setLine1] = useState(initialData?.line1 ?? '');
  const [line2, setLine2] = useState(initialData?.line2 ?? '');
  const [city, setCity] = useState(initialData?.city ?? '');
  const [state, setState] = useState(initialData?.state ?? '');
  const [postalCode, setPostalCode] = useState(initialData?.postalCode ?? '');
  const [country, setCountry] = useState(initialData?.country ?? 'United States');
  const [deliveryInstructions, setDeliveryInstructions] = useState(initialData?.deliveryInstructions ?? '');
  const [submitting, setSubmitting] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    await onSave({
      recipientName: recipientName.trim(),
      line1: line1.trim(),
      line2: line2.trim(),
      city: city.trim(),
      state: state.trim(),
      postalCode: postalCode.trim(),
      country: country.trim(),
      deliveryInstructions: deliveryInstructions.trim()
    });
    setSubmitting(false);
  };
  return <div className="bg-white border border-[#E5E5E5] p-6 md:p-8" data-id="element-1934" data-mp="82n7jwvzkpqj">
      <h2 className="text-xl font-bold text-[#111111] mb-6 tracking-tight" data-id="element-1935" data-mp="80aiz0vq3h4i">
        {initialData ? 'Edit Address' : 'Add New Address'}
      </h2>
      <form onSubmit={handleSubmit} className="space-y-5" data-id="element-1936" data-mp="3v7w1kq0u8a5c">
        <div data-id="element-1937" data-mp="40jux4xkzf3o">
          <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1938" data-mp="40a1t8h8ortr">
            Recipient Full Name *
          </label>
          <input type="text" required value={recipientName} onChange={e => setRecipientName(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1939" data-mp="3gl740tq5c71l" />
        </div>
        <div data-id="element-1940" data-mp="3lhnsxq566pfq">
          <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1941" data-mp="3lhxmtotc5gqp">
            Address Line 1 *
          </label>
          <input type="text" required value={line1} onChange={e => setLine1(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1942" data-mp="3ujx1wbksiar0" />
        </div>
        <div data-id="element-1943" data-mp="m43kzvmjqx9f">
          <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1944" data-mp="m3u3kwfkhyfc">
            Address Line 2 (Optional)
          </label>
          <input type="text" value={line2} onChange={e => setLine2(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1945" data-mp="h622bbt18zyh" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-1946" data-mp="2lasj2xiqg281">
          <div data-id="element-1947" data-mp="2pcmcqluig5yp">
            <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1948" data-mp="2pd5lpd26nk0l">
              City *
            </label>
            <input type="text" required value={city} onChange={e => setCity(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1949" data-mp="1sner7dtw5wus" />
          </div>
          <div className="grid grid-cols-2 gap-5" data-id="element-1950" data-mp="26n7d8vuh3owf">
            <div data-id="element-1951" data-mp="26jryju4pohvj">
              <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1952" data-mp="3d2go0yz6lmgu">
                State / Province *
              </label>
              <input type="text" required value={state} onChange={e => setState(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1953" data-mp="3i003g27m85ad" />
            </div>
            <div data-id="element-1954" data-mp="3ie3x6yxmtl5x">
              <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1955" data-mp="3idlrzcszrwlv">
                ZIP / Postal *
              </label>
              <input type="text" required value={postalCode} onChange={e => setPostalCode(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1956" data-mp="39egxchkjwnez" />
            </div>
          </div>
        </div>
        <div data-id="element-1957" data-mp="2vgag8d3tn220">
          <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1958" data-mp="2vgsc38mkne9e">
            Country *
          </label>
          <input type="text" required value={country} onChange={e => setCountry(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1959" data-mp="xn3fqpf7yxnp" />
        </div>
        <div data-id="element-1960" data-mp="2ryw9pssw9osu">
          <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1961" data-mp="2e5mzxr11b19s">
            Delivery Instructions (Optional)
          </label>
          <textarea rows={3} value={deliveryInstructions} onChange={e => setDeliveryInstructions(e.target.value)} placeholder="e.g. Leave at the front porch, gate code #1234, ring the doorbell." className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] placeholder:text-[#9A9A90] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all resize-none" data-id="element-1962" data-mp="2i89ysbw4hmgh" />
          <p className="text-xs text-[#9A9A90] mt-2 leading-relaxed" data-id="element-1963" data-mp="31teg55pp3ygc">
            Help the courier find you — access codes, drop-off spots, or
            anything else they should know.
          </p>
        </div>

        <div className="flex items-center gap-3 pt-4" data-id="element-1964" data-mp="1qdpr88svspcw">
          <button type="submit" disabled={submitting} className="px-6 py-3.5 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors rounded-md shadow-sm disabled:opacity-60" data-id="element-1965" data-mp="1hy0ls8t76m0a">
            {submitting ? 'Saving...' : 'Save Address'}
          </button>
          <button type="button" onClick={onCancel} disabled={submitting} className="px-6 py-3.5 bg-white border border-[#E5E5E5] text-[#111111] text-sm font-bold hover:border-tealslate hover:text-tealslate transition-colors rounded-md shadow-sm disabled:opacity-60" data-id="element-1966" data-mp="1mxmxuexahw7i">
            Cancel
          </button>
        </div>
      </form>
    </div>;
}
function CartTab() {
  const {
    items,
    subtotal,
    itemCount,
    removeItem,
    updateQuantity
  } = useCart();
  const navigate = useNavigate();
  if (items.length === 0) {
    return <div className="bg-white border border-[#D1D1D1] rounded-md p-12 text-center" data-id="element-1967" data-mp="ggrmtbjocg4r">
        <div className="w-16 h-16 bg-[#F9F9F9] border border-[#E5E5E5] rounded-full flex items-center justify-center mx-auto mb-5" data-id="element-1968" data-mp="bhyb9ony6z78">
          <Package className="w-7 h-7 text-[#9A9A90]" data-id="element-1969" data-mp="3m6vp5a0i3rev" />
        </div>
        <h3 className="text-xl font-bold text-[#111111] tracking-tight mb-3" data-id="element-1970" data-mp="3r56yzkqsq1x3">
          Your cart is empty.
        </h3>
        <p className="text-sm font-medium text-[#5A5A5A] mb-8 max-w-sm mx-auto leading-relaxed" data-id="element-1971" data-mp="3w1xarvvkibcp">
          Browse the shop to add items.
        </p>
        <div className="flex justify-center" data-id="element-1972" data-mp="8z2b19dknz4e">
          <Link to="/shop" className="px-6 py-3.5 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors rounded-md shadow-sm inline-flex items-center justify-center gap-2" data-id="element-1973" data-mp="8xzi6vwovmlu">
            Shop Supplements <ArrowRight className="w-4 h-4" data-id="element-1974" data-mp="iqf23yrjql9d" />
          </Link>
        </div>
      </div>;
  }
  return <div className="space-y-6" data-id="element-1975" data-mp="nphxerhq5d45">
      <div data-id="element-1976" data-mp="smrwyzytv55p">
        <h2 className="text-3xl font-bold text-[#111111] tracking-tight" data-id="element-1977" data-mp="sn1jueoksw76">
          Shopping Cart
        </h2>
      </div>

      <div className="bg-white border border-[#D1D1D1] rounded-md" data-id="element-1978" data-mp="wowlxlebzdm0">
        {/* Column header */}
        <div className="px-6 py-3 flex items-center justify-end" data-id="element-1979" data-mp="2h6xebo6yisgy">
          <span className="text-sm font-medium text-[#9A9A90]" data-id="element-1980" data-mp="2d411yf80fslg">Price</span>
        </div>

        <ul data-id="element-1981" data-mp="2857mwid2rtj6">
          {items.map(item => {
          const image = getItemImage(item as unknown as OrderLineItem);
          return <li key={item.productId} className="px-6 py-6 flex gap-5" data-id="element-1982" data-mp="23765s2z7m4ac">
                {/* Image */}
                {image && <div className="w-28 h-28 sm:w-32 sm:h-32 shrink-0 flex items-center justify-center" data-id="element-1983" data-mp="30stpbnakgs4g">
                    <img src={image} alt="" className="max-w-full max-h-full object-contain" data-id="element-1984" data-mp="2vwd16t3dadrd" />
                  </div>}

                {/* Details */}
                <div className="flex-1 min-w-0" data-id="element-1985" data-mp="2m58mzvlimwxl">
                  {item.type === 'book' ? <Link to="/shop" className="text-base sm:text-lg font-bold text-[#0056b3] hover:text-[#004494] hover:underline leading-snug block transition-colors" data-id="element-1986" data-mp="3jkmk9n974net">
                      {item.name}
                    </Link> : <Link to={`/shop/${getItemShopId(item as unknown as OrderLineItem) || item.productId}`} className="text-base sm:text-lg font-bold text-[#0056b3] hover:text-[#004494] hover:underline leading-snug block transition-colors" data-id="element-1987" data-mp="dbo9in4wz1z">
                      {item.name}
                    </Link>}

                  {/* Controls row */}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-3 mt-4" data-id="element-1988" data-mp="j2rqqbidr1l1">
                    <div className="flex items-center border border-[#E5E5E5] rounded-md overflow-hidden" data-id="element-1989" data-mp="e35g89txrzfx">
                      <button onClick={() => {
                    if (item.quantity === 1) {
                      removeItem(item.productId);
                    } else {
                      updateQuantity(item.productId, item.quantity - 1);
                    }
                  }} className={`px-3 py-1.5 transition-colors ${item.quantity === 1 ? 'text-[#5A5A5A] hover:bg-burgundy/10 hover:text-burgundy' : 'text-[#5A5A5A] hover:bg-[#F9F9F9]'}`} aria-label={item.quantity === 1 ? 'Remove item' : 'Decrease quantity'} data-id="element-1990" data-mp="9c6rcluvtt12">
                        {item.quantity === 1 ? <Trash2 className="w-4 h-4" data-id="element-1991" data-mp="2koduxs7dqypu" /> : <Minus className="w-4 h-4" data-id="element-1992" data-mp="2opfmrnyyw8hs" />}
                      </button>
                      <span className="px-4 text-sm font-bold text-[#111111] min-w-[2rem] text-center" data-id="element-1993" data-mp="2ork2r4a54xyf">
                        {item.quantity}
                      </span>
                      <button onClick={() => updateQuantity(item.productId, item.quantity + 1)} className="px-3 py-1.5 text-[#5A5A5A] hover:bg-[#F9F9F9] transition-colors" aria-label="Increase quantity" data-id="element-1994" data-mp="2yn3vxixblijh">
                        <Plus className="w-4 h-4" data-id="element-1995" data-mp="38dyi6nakgi4x" />
                      </button>
                    </div>

                    <span className="w-px h-5 bg-[#E5E5E5] hidden sm:block" data-id="element-1996" data-mp="3ce8l1r32asfk" />

                    <button onClick={() => removeItem(item.productId)} className="cart-delete-button text-sm font-medium text-[#5A5A5A] hover:text-burgundy transition-colors inline-flex items-center gap-1.5" data-id="element-1997" data-mp="3hfft7r949tmr">
                      <Trash2 className="w-4 h-4" data-id="element-1998" data-mp="1mbhrwb0qpvkg" />
                      Delete
                    </button>
                  </div>
                </div>

                {/* Price */}
                <div className="shrink-0 text-right" data-id="element-1999" data-mp="1w1jk9f04m056">
                  <p className="text-base sm:text-lg font-bold text-[#111111]" data-id="element-2000" data-mp="1ra1f0er2ldee">
                    {formatPrice(item.unitPrice * item.quantity)}
                  </p>
                </div>
              </li>;
        })}
        </ul>

        {/* Subtotal line */}
        <div className="px-6 py-5 text-right" data-id="element-2001" data-mp="20zbdcccibsay">
          <p className="text-lg text-[#111111]" data-id="element-2002" data-mp="2ezdo7emedlgp">
            Subtotal ({itemCount} {itemCount === 1 ? 'item' : 'items'}):{' '}
            <span className="font-bold" data-id="element-2003" data-mp="2a1kkqtchb5k4">{formatPrice(subtotal)}</span>
          </p>
        </div>
      </div>

      {/* Checkout block */}
      <div className="mt-6" data-id="element-2004" data-mp="knp7p80ipau7">
        <p className="text-sm text-[#5A5A5A] mb-4 text-right" data-id="element-2005" data-mp="fmrqoyr8umua">
          Shipping and taxes calculated at checkout.
        </p>
        <button onClick={() => navigate('/checkout-v2')} className="w-full px-8 py-4 bg-tealslate text-white font-bold hover:bg-navyslate transition-colors rounded-md shadow-sm inline-flex items-center justify-center gap-3 text-[18px]" data-id="element-2006" data-mp="2rawlkicgka5e">
          Checkout 
        </button>
      </div>
    </div>;
}